import { ThemeEvents } from "@theme/events";

/**
 * Variant-aware "Inhoud"/servings badge.
 *
 * Renders server-side with the selected variant's contents (see
 * snippets/variant-contents-badge.liquid) and swaps its content on
 * `variant:update` by reading the freshly rendered badge from the event's
 * section HTML — mirroring the product-badge component.
 */
class VariantContentsBadge extends HTMLElement {
  connectedCallback() {
    this.section = this.closest(".shopify-section, dialog");
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.#updateBadge);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.#updateBadge);
  }

  /**
   * Updates the badge from the variant-update event.
   * @param {CustomEvent} event - The variant update event.
   */
  #updateBadge = (event) => {
    // Ignore updates meant for another product (e.g. combined listings).
    if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    // Cache hit without HTML yet — the background fetch dispatches again with HTML.
    const html = event.detail?.data?.html;
    if (!html) return;

    const productId = this.dataset.productId;
    let source = null;

    for (const badge of html.querySelectorAll("variant-contents-badge")) {
      if (badge.dataset.productId === productId) {
        source = badge;
        break;
      }
    }

    source ??= html.querySelector("variant-contents-badge");
    if (!source) return;

    if (this.innerHTML.trim() !== source.innerHTML.trim()) {
      this.innerHTML = source.innerHTML;
    }
  };
}

if (!customElements.get("variant-contents-badge")) {
  customElements.define("variant-contents-badge", VariantContentsBadge);
}
